<template>
  <v-dialog v-model="show" max-width="800" persistent>
    <v-card class="alternate has--title">
      <v-card-title class="d-flex justify-space-between align-center">
        <h2 class="flex-grow">{{ $t('register.title') }}</h2>
        <v-btn small fab class="secondary" @click="close"
          ><v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="registered" type="success" text>
          {{ $t('register.checkEmail') }}
        </v-alert>

        <template v-else>
          <div
            class="d-flex flex-wrap align-center justify-space-between mb-4"
          >
            <span class="mr-4">{{ $t('register.alreadyHaveAccount') }}</span>
            <v-btn color="primary" outlined @click="goToLogin">
              <v-icon left>mdi-login</v-icon>
              {{ $t('register.login') }}
            </v-btn>
          </div>

          <v-divider class="mb-6" />

          <p>{{ $t('register.description') }}</p>

          <v-alert v-if="errorKey" type="error" text dense class="mb-4">
            {{ $t(`register.errors.${errorKey}`) }}
          </v-alert>

          <ValidationObserver ref="observer">
            <v-form @submit.prevent="onSubmit">
              <v-row>
                <v-col cols="12" md="6">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="salutation"
                    rules="required"
                    :custom-messages="{
                      required: $t('validation.salutationIsMandatory')
                    }"
                  >
                    <v-select
                      v-model="form.salutationId"
                      :items="genderOptions"
                      outlined
                      flat
                      hide-details="auto"
                      item-text="label"
                      item-value="value"
                      :label="`${$t('application.salutation')}*`"
                      :menu-props="{ bottom: true, offsetY: true }"
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="firstName"
                    rules="required"
                    :custom-messages="{
                      required: $t('validation.firstNameIsMandatory')
                    }"
                  >
                    <v-text-field
                      v-model="form.firstName"
                      outlined
                      flat
                      hide-details="auto"
                      autocomplete="given-name"
                      :label="`${$t('application.firstName')}*`"
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="lastName"
                    rules="required"
                    :custom-messages="{
                      required: $t('validation.lastNameIsMandatory')
                    }"
                  >
                    <v-text-field
                      v-model="form.lastName"
                      outlined
                      flat
                      hide-details="auto"
                      autocomplete="family-name"
                      :label="`${$t('application.lastName')}*`"
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="email"
                    rules="required|email"
                    mode="eager"
                    :custom-messages="{
                      required: $t('validation.emailInvalid'),
                      email: $t('validation.emailInvalid')
                    }"
                  >
                    <v-text-field
                      v-model="form.email"
                      outlined
                      flat
                      hide-details="auto"
                      type="email"
                      autocomplete="email"
                      :label="`${$t('application.emailAddress')}*`"
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="password"
                    rules="required"
                    :custom-messages="{ required: $t('pleaseProvide') }"
                  >
                    <v-text-field
                      v-model="form.password"
                      outlined
                      flat
                      hide-details="auto"
                      type="password"
                      :label="`${$t('register.password')}*`"
                      :hint="$t('register.passwordHint')"
                      persistent-hint
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.passwordRepeat"
                    outlined
                    flat
                    hide-details="auto"
                    type="password"
                    :label="`${$t('register.passwordRepeat')}*`"
                    :error-messages="passwordRepeatErrors"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <v-btn
                    type="reset"
                    class="secondary"
                    :block="$vuetify.breakpoint.smAndDown"
                    @click.prevent="close(false)"
                  >
                    {{ $t('cancel') }}
                  </v-btn>
                </v-col>
                <v-col cols="12" md="6" class="text-right">
                  <v-btn type="submit" color="primary" :loading="submitting">
                    {{ $t('register.submit') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </ValidationObserver>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
  name: 'RegisterDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    submitting: false,
    registered: false,
    errorKey: null,
    form: {
      salutationId: null,
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      passwordRepeat: ''
    }
  }),

  computed: {
    ...mapState('companyModule', {
      slug: 'slug'
    }),

    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },

    genderOptions() {
      return [
        { label: this.$t('application.salutations.male'), value: 1 },
        { label: this.$t('application.salutations.female'), value: 2 },
        { label: this.$t('application.salutations.others'), value: 4 }
      ];
    },

    passwordsMatch() {
      return this.form.password === this.form.passwordRepeat;
    },

    passwordRepeatErrors() {
      if (!this.form.passwordRepeat || this.passwordsMatch) {
        return [];
      }

      return [this.$t('register.passwordMismatch')];
    }
  },

  methods: {
    ...mapActions('candidateModule', ['register', 'login']),

    close() {
      this.show = false;
    },

    goToLogin() {
      // Sign in via Keycloak and return to the cockpit afterwards.
      const redirectUri = this.slug
        ? `${window.location.origin}/${this.slug}/cockpit`
        : window.location.origin;

      this.login(redirectUri);
    },

    async onSubmit() {
      this.errorKey = null;

      const valid = await this.$refs.observer.validate();
      if (!valid || !this.passwordsMatch) {
        return;
      }

      this.submitting = true;
      const result = await this.register({
        salutationId: this.form.salutationId,
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        email: this.form.email,
        password: this.form.password
      });
      this.submitting = false;

      if (result && result.success) {
        this.registered = true;
      } else {
        this.errorKey = (result && result.error) || 'failed';
      }
    }
  }
};
</script>
